import { useMemo, useState } from "react";
import { Book, Users, Upload, Home, LogOut, Settings, User, ChevronsUpDown, Store, UserMinus } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Edit3Icon, EyeIcon, MoreVerticalIcon, Redo2Icon, Trash2Icon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useUser } from "@/hooks/useUser";
import { useShopStore } from "@/store/shop";
import { useAuthStore } from "@/store/auth";
import Logo from "@/components/layout/logo";
import { ROUTES } from "@/config/routes";
import ChangePasswordDialog from "@/components/ui/change-password-dialog";
import { SESSION_NAME } from '@/config/routes';

const menuItems = [
  {
    title: "Employee",
    items: [
      {
        title: '임직원 목록 관리',
        path: ROUTES.DASHBOARD.EMPLOYEE.LIST,
        icon: Users,
      },
      {
        title: '임직원 삭제 목록',
        path: ROUTES.DASHBOARD.EMPLOYEE.DELETE,
        icon: UserMinus,
      },
      {
        title: '임직원 엑셀 업로드',
        path: ROUTES.DASHBOARD.EMPLOYEE.UPLOAD,
        icon: Upload,
      },
    ]
  },
]

export function AppSidebar() {
  const { user, logout } = useUser();
  const { shopList, selectedShop, setSelectedShop } = useShopStore(); 
  const { user: authUser } = useAuthStore();
  const location = useLocation();
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
  const navigate = useNavigate();

  // cafe24 플랫폼 사용자인지 확인
  const isCafe24User = authUser?.platform === 'cafe24';
    
  const handleLogout = () => {
    logout();
  };

  const handleChangePasswordDialogOpen = () => {
    setIsChangePasswordDialogOpen(true);
  };
  
  const handleChangePasswordDialogClose = (open: boolean) => {
    setIsChangePasswordDialogOpen(open);
  };

  const handleUserManagementOpen = () => {
    navigate(ROUTES.DASHBOARD.USER.MANAGEMENT);
  }

  const handleGuideOpen = () => {
    window.open('https://coggiri.gitbook.io/cworks/', '_blank', 'noopener,noreferrer');
  };

  const handleManagerOpen = () => {
    const authStorage = window.sessionStorage.getItem(SESSION_NAME);
    let authToken = null;
    if (authStorage) {
      const authData = JSON.parse(authStorage);
      authToken = authData?.state?.token;
    }
    window.open(`https://app.coggiri.ai.kr/cworks/manager?token=${authToken}`, '_blank', 'noopener,noreferrer');
  }

  const computedMenuItems = useMemo(() => {
    return menuItems.map((group) => ({
      ...group,
      items: [
        ...group.items
      ],
    }));
  }, [user.isSuperAdmin]);

  return (
    <Sidebar  collapsible="icon" variant="floating" className="bg-background">
      <SidebarHeader className="items-center justify-center pt-3 transition-all group-data-[collapsible=icon]:pt-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="hover:text-foreground rounded-none group-data-[collapsible=icon]:px-0! hover:bg-[var(--primary)]/10">
                  {isCafe24User && selectedShop ? <Store style={{width: '30px'}} /> : <Logo />}
                  <div className="truncate font-semibold group-data-[collapsible=icon]:hidden">
                    {isCafe24User && selectedShop ? selectedShop.storeName ? selectedShop.storeName : selectedShop.shopName : "임직원 관리 시스템"}
                  </div>
                  <ChevronsUpDown className="ml-auto group-data-[collapsible=icon]:hidden" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-(--radix-popper-anchor-width)">
                {isCafe24User && shopList.length > 0 ? (
                  <>
                    <DropdownMenuLabel className="px-2 py-1.5 text-xs text-muted-foreground">
                      쇼핑몰 선택
                    </DropdownMenuLabel>
                    {shopList.map((shop) => (
                      <DropdownMenuItem 
                        key={shop.shopNo}
                        onClick={() => setSelectedShop(shop)}
                        className={`cursor-pointer ${selectedShop?.shopNo === shop.shopNo ? 'bg-accent' : ''}`}
                      >
                        <Store className="mr-2" style={{width: '30px'}} />
                        <div className="flex flex-col">
                          <span className="font-medium">{shop.storeName ? shop.storeName : shop.shopName}</span>
                          {shop.shopDescription && (
                            <span className="text-xs text-muted-foreground">{shop.shopDescription}</span>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </>
                ) : (
                  <>
                    <DropdownMenuItem>
                      <span>임직원 관리 시스템</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {computedMenuItems.map((item) => (
        <SidebarGroup key={item.title}>
          <SidebarGroupLabel>{item.title}</SidebarGroupLabel>
          <SidebarGroupContent>
              <SidebarMenu>
                {item.items.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                        asChild
                        isActive={location.pathname === item.path}
                      >
                    <Link to={item.path}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                ))}
          </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <Card className="bg-muted gap-4 overflow-hidden py-4 group-data-[collapsible=icon]:hidden">
          <CardHeader className="px-3">
            <CardTitle>이용가이드</CardTitle>
            <CardDescription>
              임직원 관리 앱 서비스 <br />
              사용 방법을 확인해 보세요.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-3">
            <Button className="w-full" asChild>
              <Link to="https://coggiri.gitbook.io/cworks/" target="_blank" rel="noopener noreferrer">
                이용가이드 보기
              </Link>
            </Button>
          </CardContent>
          </Card>
          <SidebarMenu className='flex w-full min-w-0 flex-col gap-1'>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton 
                      size="lg"
                      className="h-12 w-full justify-start bg-transparent hover:bg-sidebar-accent focus-visible:ring-0 focus-visible:ring-offset-0"
                    >
                      <Avatar>
                          <AvatarImage src="/assets/avatars/cworks.png" alt="User" />
                          <AvatarFallback className="rounded-lg">
                            {selectedShop?.companyName?.slice(0, 2)?.toUpperCase() || "GU"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-semibold">{selectedShop?.companyName}</span>
                          <span className="text-muted-foreground truncate text-xs">{user.email}</span>
                        </div>
                        <MoreVerticalIcon /> 
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" side="right">
                    <DropdownMenuLabel className="p-0">
                      <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                        <Avatar>
                          <AvatarImage src="/assets/avatars/cworks.png" alt="User" />
                          <AvatarFallback className="rounded-lg">
                            {selectedShop?.companyName?.slice(0, 2)?.toUpperCase() || "GU"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="grid flex-1 text-left text-sm leading-tight">
                          <span className="truncate font-semibold">{selectedShop?.companyName}</span>
                          <span className="text-muted-foreground truncate text-xs">{user.email}</span>
                          {user.platformMallId && (
                            <span className="text-muted-foreground truncate text-xs" style={{color: "gray"}}>({user.platformMallId})</span>
                          )}
                        </div>
                        
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuItem onClick={handleChangePasswordDialogOpen} className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        비밀번호 재설정
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleGuideOpen} className="cursor-pointer">
                      <Book className="mr-2 h-4 w-4" />
                      이용가이드
                    </DropdownMenuItem>
                    {user.isManager && (
                      <DropdownMenuItem onClick={handleManagerOpen}  className="cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        관리자 페이지
                      </DropdownMenuItem> 
                    )}
                     {user.isSuperAdmin && (
                      <DropdownMenuItem onClick={handleUserManagementOpen}  className="cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        사용 계정 관리
                      </DropdownMenuItem> 
                    )}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="cursor-pointer" onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      로그아웃
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        
        {/* 비밀번호 재설정 다이얼로그 */}
        <ChangePasswordDialog
          open={isChangePasswordDialogOpen}
          onOpenChange={handleChangePasswordDialogClose}
        />
    </Sidebar>
  )
}
